import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { runEvScreenFree, getGamesDataFree } from '../../../utils/api/evScreen';
import { getWagerWizDisplayOptions, DEFAULT_SPORTSBOOKS } from '../../../utils/constants';
import { IOSSwitch } from '../../../components/IOSSwitch';
import '../EVScreen/evScreen.css';
import play_icon from '../../../assets/images/play_icon.svg';
import filter_icon from '../../../assets/images/filter_icon.svg';
import { EVScreenTable } from '../EvScreenTable/evScreenTable';
import wwlogo_gif from '../../../assets/ww_loading2.gif';
import { getFutureDate } from '../../../utils/getFutureDate';
import { UpgradeModal } from '../../../components/UpgradeModal/UpgradeModal';
import { useAuth0 } from '@auth0/auth0-react';
import { FiltersContainer } from '../Filters/FiltersContainer';

const FREE_DAILY_SCAN_LIMIT = 8;
const FREE_SCAN_COUNT_KEY = 'free_ev_scan_count';

function getTodayDateStr() {
	return new Date().toISOString().slice(0, 10);
}

function getFreeDailyScanCount() {
	try {
		const raw = localStorage.getItem(FREE_SCAN_COUNT_KEY);
		if (!raw) return 0;
		const parsed = JSON.parse(raw);
		if (parsed.date !== getTodayDateStr()) return 0;
		return parsed.count || 0;
	} catch {
		return 0;
	}
}

function incrementFreeDailyScanCount() {
	const today = getTodayDateStr();
	const current = getFreeDailyScanCount();
	localStorage.setItem(FREE_SCAN_COUNT_KEY, JSON.stringify({ date: today, count: current + 1 }));
}

function isFreeDailyScanLimitReached() {
	return getFreeDailyScanCount() >= FREE_DAILY_SCAN_LIMIT;
}

export const FreeEvScreen = (props) => {
	const { isMobile, hideSignupBanner } = props;
	const [isLoading, setIsLoading] = useState(false);
	const [data, setData] = useState(false);
	const [isLive, setIsLive] = useState(false);
	const [relatedOdds, setRelatedOdds] = useState();
	const [selectedRow, setSelectedRow] = useState();
	const [order, setOrder] = useState('desc');
	const [orderBy, setOrderBy] = useState('ev');
	const [isScansLimitModalOpen, setIsScansLimitOpen] = useState(false);
	const [scansRemaining, setScansRemaining] = useState(FREE_DAILY_SCAN_LIMIT - getFreeDailyScanCount());
	const { loginWithRedirect } = useAuth0();
	const [filtersOpen, setFiltersOpen] = useState(false);

	const date = getFutureDate(7);

	const [filters, setFilters] = useState([
		{
			sports: [],
			leagues: [],
			sportsbooks: DEFAULT_SPORTSBOOKS,
			sharpBooks: getWagerWizDisplayOptions(),
			markets: [],
			marketTypes: [],
			minMaxEv: [1, 9999999],
			minMaxOdds: [-9999999, 9999999],
			date: date || new Date(),
			dateDifference: 7,
		},
	]);

	const [pagination, setPagination] = useState({
		page: 1,
		limit: 50,
		total: 0,
		totalPages: 0,
		hasNext: false,
		hasPrev: false,
	});

	const executeFreeScan = async () => {
		if (isFreeDailyScanLimitReached()) {
			setIsScansLimitOpen(true);
			return;
		}

		setIsLoading(true);

		const filtersCopy = JSON.parse(JSON.stringify(filters));
		for (const filter of filtersCopy) {
			if (!filter.sharpBooks || !filter.sharpBooks.length) {
				filter.sharpBooks = getWagerWizDisplayOptions();
			}
		}

		const evrunres = await runEvScreenFree(filtersCopy, isLive);

		if (evrunres === 'exceeded daily scans limit') {
			setIsScansLimitOpen(true);
			setIsLoading(false);
			return;
		}

		if (!evrunres) {
			setIsLoading(false);
			return;
		}

		incrementFreeDailyScanCount();
		setScansRemaining(FREE_DAILY_SCAN_LIMIT - getFreeDailyScanCount());

		const { data: evData, pagination: paginationData } = evrunres;
		const d = JSON.parse(JSON.stringify(evData));

		const gameIds = [];
		for (const evdataRow of d) {
			if (evdataRow.outcome_id_normalized_no_book && !evdataRow.outcome_id_normalized) {
				evdataRow.outcome_id_normalized = evdataRow.outcome_id_normalized_no_book;
			}
			if (evdataRow.market !== 'Point Spread' && evdataRow.market !== 'Asian Handicap') {
				if (evdataRow.ev_over > evdataRow.ev_under) {
					evdataRow.ev = evdataRow.ev_over;
				} else {
					evdataRow.ev = evdataRow.ev_under;
				}
			}
			if (evdataRow.game_id && !gameIds.includes(evdataRow.game_id)) {
				gameIds.push(evdataRow.game_id);
			}
		}

		if (gameIds.length > 0) {
			try {
				const gamesData = await getGamesDataFree(gameIds);
				if (gamesData?.games) {
					for (const row of d) {
						const game = gamesData.games.find((g) => g.game_id === row.game_id);
						if (game) {
							row.game = game;
						}
					}
				}
			} catch (e) {
				console.error('Failed to fetch games data for free scanner:', e);
			}
		}

		setData(d);
		setPagination(paginationData || pagination);
		setIsLoading(false);
	};

	useEffect(() => {
		executeFreeScan();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div
			className="page flex flex-col"
			style={{ maxWidth: '100vw', overflowX: 'hidden', paddingLeft: isMobile ? '8px' : '8px', paddingRight: isMobile ? '8px' : '8px' }}>
			<Helmet>
				<title>Free +EV Scanner | Find Positive Expected Value Bets | WagerWiz</title>
				<meta name="description" content="Free positive-EV scanner. Compare sportsbook odds in real time to spot bets where the price beats the true probability. No credit card required." />
				<link rel="canonical" href="https://www.thewagerwiz.com/ev-scanner" />
				<meta property="og:title" content="Free +EV Scanner | WagerWiz" />
				<meta property="og:description" content="Find positive-EV bets across major sportsbooks in real time. Free to use." />
				<meta property="og:type" content="website" />
				<meta property="og:url" content="https://www.thewagerwiz.com/ev-scanner" />
				<meta property="og:site_name" content="WagerWiz" />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content="Free +EV Scanner | WagerWiz" />
				<meta name="twitter:description" content="Find positive-EV bets across major sportsbooks in real time. Free to use." />
			</Helmet>
			<UpgradeModal
				message={`Daily free scan limit reached (${FREE_DAILY_SCAN_LIMIT} per day). Create a free account for more scans!`}
				isOpen={isScansLimitModalOpen}
				setIsOpen={() => setIsScansLimitOpen(false)}
			/>
			<div style={{ flex: 1 }}>
				<div className="px-4 md:px-4 xl:px-6 mb-6">
					<div className="w-full max-w-[95vw] mx-auto">
						<h2 style={{ textAlign: isMobile ? 'center' : 'left' }} className="text-3xl ev-scanner-title">
							Free EV Scanner
						</h2>
						<p style={{ color: '#94a3b8', fontSize: 14, marginTop: 4 }}>
							{scansRemaining > 0
								? `${scansRemaining} free scan${scansRemaining === 1 ? '' : 's'} remaining today`
								: 'No free scans remaining today'}
						</p>
					</div>
				</div>

				{!hideSignupBanner && (
					<div
						className="px-4 md:px-4 xl:px-6 mb-4"
						style={{
							display: 'flex',
							justifyContent: 'center',
						}}>
						<div
							style={{
								width: '100%',
								maxWidth: '95vw',
								background: 'linear-gradient(135deg, #1a1d23 0%, #23272f 100%)',
								border: '1px solid #2d3748',
								borderRadius: 12,
								padding: isMobile ? '12px 16px' : '14px 24px',
								display: 'flex',
								flexDirection: isMobile ? 'column' : 'row',
								alignItems: 'center',
								justifyContent: 'space-between',
								gap: 12,
							}}>
							<span style={{ color: '#e2e8f0', fontSize: 14 }}>
								Create a free account for more scans, filters, auto-refresh, notifications, and bet tracking.
							</span>
							<button
								onClick={() =>
									loginWithRedirect({
										authorizationParams: { prompt: 'login select_account' },
									})
								}
								style={{
									background: '#2563ff',
									color: 'white',
									border: 'none',
									borderRadius: 8,
									padding: '8px 20px',
									fontSize: 14,
									fontWeight: 600,
									cursor: 'pointer',
									whiteSpace: 'nowrap',
									transition: 'background 0.2s',
								}}
								onMouseEnter={(e) => (e.currentTarget.style.background = '#1d4ed8')}
								onMouseLeave={(e) => (e.currentTarget.style.background = '#2563ff')}>
								Sign up free
							</button>
						</div>
					</div>
				)}

				{/* Controls */}
				<div className="px-4 md:px-4 xl:px-6 mb-4">
					<div
						className="w-full max-w-[95vw] mx-auto"
						style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
						<button
							className="play-button"
							onClick={executeFreeScan}
							disabled={isLoading}
							style={{
								background: '#2563ff',
								color: 'white',
								border: 'none',
								borderRadius: 8,
								padding: '10px 24px',
								fontSize: 16,
								fontWeight: 600,
								cursor: isLoading ? 'not-allowed' : 'pointer',
								display: 'flex',
								alignItems: 'center',
								gap: 8,
								opacity: isLoading ? 0.6 : 1,
								transition: 'background 0.2s, opacity 0.2s',
							}}
							onMouseEnter={(e) => !isLoading && (e.currentTarget.style.background = '#1d4ed8')}
							onMouseLeave={(e) => (e.currentTarget.style.background = '#2563ff')}>
							<img src={play_icon} alt="" style={{ width: 18, height: 18 }} />
							{isLoading ? 'Scanning...' : 'Run Scan'}
						</button>
						<button
							className="filter-button-tutorial"
							onClick={() => setFiltersOpen(!filtersOpen)}
							style={{
								background: filtersOpen ? '#374151' : '#1f2937',
								color: 'white',
								border: '1px solid #374151',
								borderRadius: 8,
								padding: '10px 20px',
								fontSize: 14,
								fontWeight: 500,
								cursor: 'pointer',
								display: 'flex',
								alignItems: 'center',
								gap: 8,
								transition: 'background 0.2s',
							}}
							onMouseEnter={(e) => (e.currentTarget.style.background = '#374151')}
							onMouseLeave={(e) => (e.currentTarget.style.background = filtersOpen ? '#374151' : '#1f2937')}>
							<img src={filter_icon} alt="" style={{ width: 16, height: 16 }} />
							Filters
						</button>
						<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
							<span style={{ color: '#94a3b8', fontSize: 14 }}>Live</span>
							<IOSSwitch checked={isLive} onChange={(e) => setIsLive(e.target.checked)} />
						</div>
					</div>
				</div>

				{filtersOpen && (
					<div className="px-4 mb-6">
						<div className="w-full max-w-[95vw] mx-auto">
							<FiltersContainer
								filters={filters}
								setFilters={setFilters}
								filtersOpen={filtersOpen}
								isMobile={isMobile}
								setFiltersOpen={setFiltersOpen}
								isScannable={true}
								executeEvScreen={executeFreeScan}
							/>
						</div>
					</div>
				)}

				<div className="px-4 md:px-4 xl:px-6 pt-4">
					<div className="w-full max-w-[95vw] mx-auto">
						<EVScreenTable
							data={data}
							isLoading={isLoading}
							bankroll={10000}
							relatedOdds={relatedOdds}
							setRelatedOdds={setRelatedOdds}
							preloadedRelatedOdds={new Map()}
							selectedRow={selectedRow}
							setSelectedRow={setSelectedRow}
							allBetsOpen={'all'}
							isLive={isLive}
							isAutoRefresh={false}
							loadingIcon={wwlogo_gif}
							isMobileProp={isMobile}
							order={order}
							setOrder={setOrder}
							orderBy={orderBy}
							setOrderBy={setOrderBy}
							sharpFilter={'WagerWiz'}
							freeRowLimit={5}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};
